import React, { useState } from 'react';
import {
    Alert, AlertTitle, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import bulkAudioStore from '../../Mobx/BulkAudioStore';
import { formatError } from '../../Mobx/AudioCatalog';
import {
    MODE_LABELS, PROVIDER_LABELS, describeJob, formatDateTime, formatNumber, formatUsd, groupSizeOf, isReplaceMode, isTrialMode, modeOf, progressOf
} from './bulkAudioForm';

const HEADERS = ['התחילה', 'מצב', 'סטטוס', 'התקדמות', 'נוצרו / הוחלפו', 'כבר היו / אין קובץ', 'נכשלו', 'עלות', 'ספק', ''];

/** The color of a chip for the severity that describeJob gives */
export const chipColor = (severity) => (['info', 'warning', 'success', 'error'].includes(severity) ? severity : 'default');

/** A question before an action that cannot be taken back: the title, the text, and the button that does it */
export const ConfirmDialog = ({ open, title, children, confirmLabel, busy, onCancel, onConfirm, testId }) => (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>{children}</DialogContent>
        <DialogActions>
            <Button onClick={onCancel} disabled={busy}>ביטול</Button>
            <Button variant="contained" onClick={onConfirm} disabled={busy} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null} data-testid={testId}>
                {confirmLabel}
            </Button>
        </DialogActions>
    </Dialog>
);

/**
 * The latest runs (the page refreshes them while it is open). From here a run is opened (details, files, resume) or asked to stop.
 */
const BulkRunJobs = observer(({ onOpen }) => {
    const store = bulkAudioStore;
    const { jobs, jobsLoading, jobsError, actionError, actionJobId } = store;
    const [stopping, setStopping] = useState(null);

    const confirmStop = async () => {
        await store.stopJob(stopping.id);
        setStopping(null);
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="bulk-jobs-card">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="h6">הרצות אחרונות</Typography>
                <Button size="small" variant="outlined" onClick={() => store.loadJobs()} disabled={jobsLoading} startIcon={jobsLoading ? <CircularProgress size={14} /> : null}>
                    רענון
                </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                הרשימה מתרעננת מעצמה בזמן שהלשונית פתוחה. ההרצה רצה בשרת, אז אפשר לסגור את הדף ולחזור אליו אחר כך.
            </Typography>

            {jobsError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    <AlertTitle>טעינת ההרצות נכשלה</AlertTitle>
                    <Box dir="auto">{formatError(jobsError)}</Box>
                </Alert>
            )}
            {actionError && !stopping && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => store.clearActionError()}>
                    <Box dir="auto">{formatError(actionError)}</Box>
                </Alert>
            )}
            {jobs && jobs.length === 0 && !jobsError && <Alert severity="info">עוד לא היו הרצות.</Alert>}

            {jobs && jobs.length > 0 && (
                <TableContainer>
                    <Table size="small" aria-label="הרצות אחרונות">
                        <TableHead>
                            <TableRow>
                                {HEADERS.map((header, index) => <TableCell key={header || index} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{header}</TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody data-testid="bulk-jobs-rows">
                            {jobs.map((job) => {
                                const status = describeJob(job);
                                const progress = progressOf(job);
                                return (
                                    <TableRow key={job.id} data-job-id={job.id} data-status={job.stalled ? 'stalled' : job.status}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}><bdi>{formatDateTime(job.startedAt ?? job.createdAt)}</bdi></TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }} data-testid={`bulk-mode-${job.id}`}>
                                            {MODE_LABELS[modeOf(job)] ?? modeOf(job)}
                                            {groupSizeOf(job) !== null && <Typography variant="caption" component="div" color="text.secondary">בקבוצות של עד {formatNumber(groupSizeOf(job))}</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" color={chipColor(status.severity)} label={status.label} />
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 160 }}>
                                            <LinearProgress variant="determinate" value={progress.percent} aria-label="התקדמות ההרצה" />
                                            <Typography variant="caption"><bdi>{formatNumber(progress.finished)} / {formatNumber(progress.total)}</bdi></Typography>
                                        </TableCell>
                                        <TableCell>{formatNumber(job.counters?.done)}</TableCell>
                                        <TableCell>{formatNumber(job.counters?.skipped)}</TableCell>
                                        <TableCell sx={{ color: (job.counters?.failed ?? 0) > 0 ? 'error.main' : undefined }}>{formatNumber(job.counters?.failed)}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}><bdi>{formatUsd(job.estimatedCostUsd)} / {formatUsd(job.maxCostUsd)}</bdi></TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {PROVIDER_LABELS[job.config?.provider] ?? job.config?.provider ?? ''}
                                            {job.switchedToFallback ? ' (עבר לגיבוי)' : ''}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            <Button size="small" onClick={() => onOpen(job.id)} data-testid={`bulk-open-${job.id}`}>פרטים</Button>
                                            {job.canStop && !job.stopRequested && (
                                                <Button size="small" color="warning" onClick={() => { store.clearActionError(); setStopping(job); }} data-testid={`bulk-stop-${job.id}`}>עצירה</Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ConfirmDialog
                open={Boolean(stopping)}
                title="לעצור את ההרצה?"
                confirmLabel="עצירה"
                busy={Boolean(actionJobId)}
                onCancel={() => setStopping(null)}
                onConfirm={confirmStop}
                testId="bulk-confirm-stop"
            >
                <Typography>
                    הקבצים שנמצאים בעבודה כרגע יסתיימו, ולא יתחילו קבצים נוספים.
                    {isReplaceMode(modeOf(stopping)) ? ' הקבצים שכבר הוחלפו נשארים מוחלפים, והגיבוי שלהם נשמר.' : (isTrialMode(modeOf(stopping)) ? ' הקבצים שכבר נוצרו נשארים בתיקיית הניסוי.' : ' הקבצים שכבר נוצרו נשארים.')}
                    {groupSizeOf(stopping) !== null ? ' בקשה של קבוצה שכבר נשלחה מסתיימת (היא כבר שולמה), ולכן העצירה יכולה לקחת כמה דקות.' : ''}
                    {' '}אפשר להמשיך את ההרצה אחר כך.
                </Typography>
                {actionError && <Alert severity="error" sx={{ mt: 1 }}><Box dir="auto">{formatError(actionError)}</Box></Alert>}
            </ConfirmDialog>
        </Paper>
    );
});

export default BulkRunJobs;
